import { useEffect, useMemo, useState } from 'react'
import { Modal } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { type ILoanSubledgerRow, emptyLoanSubledgerRow } from '../WarehouseLines/types'
import { loanSubledgerInputs } from './constants'

export const EditLoanSubledgerDialog = ({
  item,
  warehouseLineOptions,
  onClose,
  onSave,
}: {
  item: Partial<ILoanSubledgerRow> | null
  warehouseLineOptions: Record<string, string>
  onClose: () => void
  onSave: (data: Partial<ILoanSubledgerRow>) => Promise<void>
}) => {
  const [form, setForm] = useState<Partial<ILoanSubledgerRow>>(emptyLoanSubledgerRow())
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!item) return
    setForm({
      ...emptyLoanSubledgerRow(),
      ...item,
      noteRate: item.noteRate != null ? Number(item.noteRate) * 100 : 0,
      fundedDate: item.fundedDate ? String(item.fundedDate).slice(0, 10) : '',
      status: item.status || 'Held',
      servicing: item.servicing || 'Released',
    })
  }, [item])

  const inputs = useMemo(() => loanSubledgerInputs(form, warehouseLineOptions), [form, warehouseLineOptions])

  if (!item) return null

  const onChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const onSubmit = async () => {
    setSaving(true)
    try {
      await onSave({
        loanId: form.loanId,
        borrower: form.borrower,
        product: form.product,
        noteRate: Number(form.noteRate || 0) / 100,
        fundedAmount: Number(form.fundedAmount || 0),
        invAccountNo: form.invAccountNo,
        status: form.status || 'Held',
        warehouseLine: form.warehouseLine,
        salePrice: Number(form.salePrice || 0),
        investor: form.investor,
        escrowBalance: Number(form.escrowBalance || 0),
        msrValue: Number(form.msrValue || 0),
        fundedDate: form.fundedDate || null,
        servicing: form.servicing || 'Released',
      })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen
      title={item.id ? 'Edit Loan Subledger' : 'Add Loan Subledger'}
      loading={saving}
      disabled={!form.loanId?.trim()}
      onOk={onSubmit}
      onClose={onClose}
      titleOkay="Save"
      titleCancel=""
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2 max-h-[70vh] w-[500px] overflow-y-auto">
        {Object.keys(inputs).map((key) => (
          <RenderInput key={key} input={inputs[key]} Key={key} onChange={onChange} />
        ))}
      </div>
    </Modal>
  )
}
